/**
 * sequence-handoff.js
 *
 * Turns a saved Library "Sequence" (sequences/sequence_slots) into a live CS
 * PDP image-set session. A sequence carries NO product, so staging asks the
 * user for a product first (Option A), then reveals the row picker.
 *
 * Slot kinds (persisted):
 *   image-type slot:  type==='built-in', key set,  image_url null
 *   reference slot:   type==='custom',   key null,  image_url set
 *   described slot:   type==='custom',   key null,  image_url null (description set)
 *
 * Exports:
 *   resolveCanonicalKey(key)          — slot.key → a BUILT_IN_TYPES key (re-export)
 *   buildRowsFromSequence(sequence)   — pure row builder
 *   stageSequenceInCreativeStudio(adapter, sequence, sequenceId)  — async (Task 3)
 *   revealSequencePickerWithProduct(adapter, productReferences)   — async (Task 3)
 */

import { loadPdpPickerData } from './create-pdp-image-set';
import {
  reindexRowSelections,
  resolveCanonicalKey,
  resolveProductOverlayUrl,
} from './pdp-generation-paths';
import { briefWidgetFields } from '../brief-widget-fields';

// resolveCanonicalKey (and the Library→CS type fallbacks behind it) lives in
// pdp-generation-paths so the picker's preset dropdown resolves slot keys the
// same way this handoff does. Re-exported here for the existing callers.
export { resolveCanonicalKey };

/**
 * A slot's resolved source decides which card mode Creative Studio presets:
 *   'upload' → 'yours' (the user supplies the image; the card waits for it)
 *   anything else → 'ai' (generated)
 * `effective_source` is the API's resolution of the slot's own override against
 * its Image Type's default — `source` alone is null whenever the slot inherits.
 * The user can still override it per card in the picker. (POR-14020)
 */
export function slotModeFromSource(slot) {
  return (slot && (slot.effective_source || slot.source) === 'upload') ? 'yours' : 'ai';
}

/**
 * Build a `pdp_row_selections` array from a sequence's slots, preserving order.
 * - reference-image slot (image_url set) → reference row (product composed IN)
 * - image-type slot (built-in key)       → AI row of that type
 * - described custom slot                 → generic AI row guided by description
 * A slot marked `source: 'upload'` becomes a "yours" row instead, waiting on an
 * image from the user — for a reference slot, the stored image IS that image.
 */
export function buildRowsFromSequence(sequence) {
  const slots = (sequence && Array.isArray(sequence.slots)) ? sequence.slots : [];
  const rows = slots.map((slot) => {
    const direction = slot.description || null;
    const label = slot.label || 'Image';
    const mode = slotModeFromSource(slot);

    // Reference-image slot → reference row (CS places the product into the ref).
    // Marked "upload", though, the stored image is the finished image: keep it
    // as the user's own image rather than a composition base.
    if (slot.image_url) {
      if (mode === 'yours') {
        return {
          type: 'lifestyle',
          label,
          mode: 'yours',
          own_image_url: slot.image_url,
          own_image_thumb: slot.image_url,
          productMode: null,
          template_id: null,
          template_thumb: null,
          template_image_url: null,
          scene: null,
          overlayText: null,
          brandLogoUrl: null,
          userInstructions: direction,
        };
      }

      return {
        type: 'lifestyle',
        label,
        mode: 'ai',
        reference_image_url: slot.image_url,
        productMode: 'integrated',
        template_id: null,
        template_thumb: null,
        template_image_url: null,
        scene: null,
        overlayText: null,
        brandLogoUrl: null,
        userInstructions: direction,
      };
    }

    // Image-type slot OR described custom slot → AI row (or an empty "yours"
    // row the picker will ask the user to fill).
    const type = (slot.type === 'built-in' && slot.key)
      ? resolveCanonicalKey(slot.key)
      : 'lifestyle';
    return {
      type,
      label,
      mode,
      template_id: null,
      template_thumb: null,
      template_image_url: null,
      scene: null,
      productMode: null,
      original_image_url: null,
      overlayText: null,
      brandLogoUrl: null,
      userInstructions: direction,
    };
  });

  return reindexRowSelections(rows);
}

/**
 * Stage a sequence into the current (already-fresh) CS conversation.
 * Persists the built rows + a `pdp_pending_sequence` marker on parsedPrompt,
 * then asks the user for a product (typed / upload / library). The picker is
 * revealed later by revealSequencePickerWithProduct once a product exists.
 *
 * @param {object} adapter    — CS adapter
 * @param {object} sequence   — { id, name, slots:[…] }
 * @param {number|string} sequenceId
 */
export async function stageSequenceInCreativeStudio(adapter, sequence, sequenceId) {
  const rows = buildRowsFromSequence(sequence);
  const name = (sequence && sequence.name) || 'Sequence';

  const parsed = adapter.getParsedPrompt();
  adapter.setParsedPrompt({
    ...parsed,
    pdp_row_selections: rows,
    pdp_is_set: true,
    pdp_pending_sequence: { id: sequenceId, name },
  });
  await adapter.saveConversationState();

  const slotList = rows.map((r) => r.label).filter(Boolean).join(' · ');
  await adapter.addAndSaveMessage({
    role: 'assistant',
    content:
      `Loaded <b>${name}</b> — ${rows.length} image${rows.length === 1 ? '' : 's'}.`
      + `<br><span style="color:#9aa3ad">${slotList}</span>`
      + '<br><br>What product should I build these for?',
    widgets: [{
      type: 'action_buttons',
      id: 'sequence_product_ask',
      buttons: [
        { label: 'ASIN', action: 'sequence_product_asin', variant: 'light' },
        { label: 'Upload a photo', action: 'sequence_product_upload', variant: 'light' },
        { label: 'Pick from library', action: 'sequence_product_library', variant: 'light' },
      ],
    }],
  });
}

/**
 * Reveal the pre-built sequence picker once a product has been resolved.
 * Reads the rows + pending marker off parsedPrompt, sets product_references,
 * clears the marker, and shows the pdp_image_set_picker widget.
 *
 * @param {object} adapter
 * @param {Array} productReferences — resolved product refs ({type,value,display_name,…})
 */
export async function revealSequencePickerWithProduct(adapter, productReferences) {
  const parsed = adapter.getParsedPrompt();
  const rows = Array.isArray(parsed.pdp_row_selections) ? parsed.pdp_row_selections : [];
  const pending = parsed.pdp_pending_sequence || {};

  // Persist the product and clear the pending marker.
  const nextParsed = {
    ...parsed,
    product_references: productReferences || [],
    pdp_is_set: true,
  };
  nextParsed.pdp_pending_sequence = null;
  adapter.setParsedPrompt(nextParsed);
  await adapter.saveConversationState();

  adapter.setProcessing(true);
  try {
    const [pickerData, productImageUrl] = await Promise.all([
      loadPdpPickerData(adapter, productReferences || []),
      resolveProductOverlayUrl(productReferences || [], adapter.getMessages() || []).catch(() => null),
    ]);

    const hasReference = rows.some((r) => !!r.reference_image_url);
    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: '',
      widgets: [{
        type: 'pdp_image_set_picker',
        id: 'pdp_image_set_picker',
        rows,
        templatesBySubtype: pickerData.templatesBySubtype,
        presetSources: pickerData.presetSources,
        initialPresetId: pending.id != null ? `library:${pending.id}` : pickerData.initialPresetId,
        referenceMode: hasReference,
        productImageUrl,
        ...briefWidgetFields(adapter),
      }],
    });
  } finally {
    adapter.setProcessing(false);
  }
}
